// Pure decision logic for the Matterport lead gate state machine.
//
// Deliberately free of React / DOM so every branch (trigger timing, the
// check-status fork, verify outcome, validation, resend) is unit-testable in
// the repo's node test env. The component is a thin view that calls these.

import { extractOtpProof, type ApiResult, type NextAction } from "./leadGateApi";

// ── Trigger ──────────────────────────────────────────────────────────────
export interface TriggerInput {
  sweepCount: number;
  armedAt: number | null; // ms; null = not armed yet
  now: number; // ms
  nodeTarget: number;
  minDwellMs: number;
  maxWaitMs: number;
}
export interface TriggerDecision {
  show: boolean; // show right now
  delayMs: number; // if not now, schedule a re-check after this; -1 = never
}

/**
 * Show when sweeps >= target AND the minimum dwell has elapsed, OR when the
 * max-wait fallback fires. Returns the delay until the next decision point so
 * the caller can schedule a single timer.
 */
export const evaluateTrigger = (i: TriggerInput): TriggerDecision => {
  if (i.armedAt == null) return { show: false, delayMs: -1 };
  const reached = i.sweepCount >= i.nodeTarget;
  const dwellLeft = i.armedAt + i.minDwellMs - i.now;
  const maxLeft = i.armedAt + i.maxWaitMs - i.now;
  const delay = reached ? Math.max(0, dwellLeft) : Math.max(0, maxLeft);
  return { show: delay <= 0, delayMs: delay };
};

// ── check-status fork ──────────────────────────────────────────────────────
export type CheckOutcome =
  | { kind: "unlock"; customerId: string } // trusted device — skip OTP
  | { kind: "otp" } // existing customer — send OTP next
  | { kind: "name" } // brand-new lead — collect name first
  | { kind: "conflict" } // 409 — identity clash
  | { kind: "error"; message: string };

export const interpretCheckStatus = (r: ApiResult): CheckOutcome => {
  if (r.status === 409) return { kind: "conflict" };
  if (!r.ok) return { kind: "error", message: r.error || "Something went wrong. Please try again." };
  const next = (r.data?.data?.next_action || r.data?.next_action) as NextAction | undefined;
  if (next === "unlock") return { kind: "unlock", customerId: extractOtpProof(r.data).customerId };
  if (next === "send_otp") return { kind: "otp" };
  return { kind: "name" }; // collect_name / unknown -> safest is to collect a name
};

// ── verify-otp outcome ─────────────────────────────────────────────────────
export type VerifyOutcome =
  | { kind: "verified"; customerId: string }
  | { kind: "error"; message: string };

export const interpretVerify = (r: ApiResult): VerifyOutcome => {
  if (!r.ok) return { kind: "error", message: r.error || "That code didn't match. Try again." };
  return { kind: "verified", customerId: extractOtpProof(r.data).customerId };
};

// ── validation ─────────────────────────────────────────────────────────────
export const onlyDigits = (s: string) => String(s || "").replace(/\D+/g, "");
export const validatePhone = (raw: string): string | null =>
  /^\d{10}$/.test(onlyDigits(raw)) ? null : "Enter a valid 10-digit number.";
export const validateOtp = (raw: string): string | null =>
  /^\d{4,6}$/.test(onlyDigits(raw)) ? null : "Enter the code we sent you.";
export const validateName = (raw: string): string | null =>
  String(raw || "").trim() ? null : "Please tell us your name.";
export const validateEmail = (raw: string): string | null =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(raw || "").trim()) ? null : "Enter a valid email.";

// ── resend cooldown ────────────────────────────────────────────────────────
export const RESEND_COOLDOWN_S = 30;
export const canResend = (secondsLeft: number): boolean => secondsLeft <= 0;
